import {Button, Checkbox, Col, Collapse, Divider, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {isEqual} from 'underscore';
import {formatTitleCase} from "@lib/format";
import {defaultIssueFilterProps, fixFilterObjectEmptyArrays} from "@components/issues-filter-drawer/IssueFilterDrawer";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {FilterIcon} from "@components/icons/Icons";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {useGetFilterOptionsQuery} from "@mwServices/Issue";
import {transformEra} from "@lib/utils";

const { Panel } = Collapse;

const SalesListFiltersContent = ({filters, setFilters, listType}) => {
    const subscriptionType = useSubscriptionType();
    const showPriceInfo = useMemo(() => ( subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const queryParams = useMemo(() => ({filters: {showVariants: true}, listType: listType}), [listType]);
    const filterOptionsQuery = useGetFilterOptionsQuery(queryParams);

    const {publishers, eras, total_raw, total_slabbed} = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data;
        return {publishers: [], eras: [], total_raw: 0, total_slabbed: 0};
    }, [filterOptionsQuery]);

    const defaultSortTitle = useMemo(() => {
        if(listType === 'DAILY_HOT') return 'Default';
        if(listType === 'DAILY_SHAKERS') return 'Sale Price (Highest to Lowest)';
        if(listType === 'WEEKLY_MOVERS') return 'Units Sold (Highest to Lowest)';
        return 'Default';
    }, [listType]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={24} >{defaultSortTitle}</Radio>
                            <Radio value={0} >Title A-Z</Radio>
                            {listType === 'DAILY_HOT' && (<>
                                <Radio value={showPriceInfo ? 1 : null} disabled={!showPriceInfo}>Est Value (Highest to Lowest)</Radio>
                                <Radio value={showPriceInfo ? 2 : null} disabled={!showPriceInfo}>Current Market Trend (%) (Highest to Lowest)</Radio>
                            </>)}
                            <Radio value={6} >Comic Publish Date (Newest to Oldest)</Radio>
                        </Space>
                    </Radio.Group>

                    <br/>
                    {!showPriceInfo && (<p><br/><a className="upgrade-link" href="/plans/unlimited/">*Upgrade to use all filter options.</a></p>)}
                </Col>
            </Row>
            <Divider />
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={["collection-wishlist","regular-variants","all-time-high","raw-slabbed","era"]} expandIconPosition={"end"}>

                        {subscriptionType.isLoggedIn && (
                            <>
                                <Panel header="Collection / Wishlist" key={"collection-wishlist"}>
                                    <Space direction="vertical">
                                        <Checkbox
                                            checked={!!filters?.inCollection}
                                            onChange={(event) => setFilters({
                                                ...filters, inCollection:
                                                event.target.checked
                                            })}
                                        >{formatTitleCase('Items In Your Collection')}
                                        </Checkbox>
                                        <Checkbox
                                            checked={!!filters?.inWishlist}
                                            onChange={(event) => setFilters({
                                                ...filters, inWishlist:
                                                event.target.checked
                                            })}
                                        >{formatTitleCase('Items In Your Wishlist')}
                                        </Checkbox>
                                    </Space>
                                </Panel>
                            </>
                        )}

                        <Panel header="Regular / Variants" key={"regular-variants"}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!!filters?.onlyVariants}
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            onlyVariants: e.target.checked
                                        })}
                                >{formatTitleCase('Only Show Variants')}</Checkbox>
                            </Space>
                        </Panel>

                        {listType === 'DAILY_SHAKERS' && (
                            <>
                                <Panel header="All-Time High Sales (Top $)" key={"all-time-high"}>
                                    <Space direction="vertical">
                                        <Checkbox
                                            checked={!!filters?.onlyAllTimeHighSale}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    onlyAllTimeHighSale: e.target.checked
                                                })}
                                        >{formatTitleCase('Only Show New All-Time Highs')}</Checkbox>
                                    </Space>
                                </Panel>
                                <Panel header="Raw / Slabbed" key={"raw-slabbed"}>
                                    <Space direction="vertical">
                                        <Checkbox
                                            checked={!filters.hideSlabbed && !filters.hideRaw}
                                            onChange={(event) => {
                                                setFilters({
                                                    ...filters,
                                                    hideSlabbed: event.target.checked ? undefined : true,
                                                    hideRaw: event.target.checked ? undefined : true,
                                                });
                                            }}
                                        >All</Checkbox>
                                        <Checkbox
                                            checked={!filters.hideRaw}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    hideRaw: !e.target.checked
                                                })}
                                        >
                                            {formatTitleCase('Show Raw')}  ({total_raw})
                                        </Checkbox>
                                        <Checkbox
                                            checked={!filters.hideSlabbed}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    hideSlabbed: !e.target.checked
                                                })}
                                        >
                                            {formatTitleCase('Show Slabbed')}  ({total_slabbed})
                                        </Checkbox>
                                    </Space>
                                </Panel>
                            </>
                        )}

                        <Panel header="Era" key={"era"}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.multipleDateRanges}
                                    onChange={(event) => {
                                        setFilters({...filters, multipleDateRanges: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {eras.map((era, i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.multipleDateRanges || filters.multipleDateRanges.findIndex(dr => !!dr.id && dr.id == era.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.multipleDateRanges && !event.target.checked){
                                                setFilters({...filters, multipleDateRanges: eras.map(transformEra).filter(item => item.id !== era.id)});
                                            } else if(filters.multipleDateRanges) {
                                                let filterDateRanges: any = filters.multipleDateRanges ? [...filters.multipleDateRanges] : [];
                                                if (event.target.checked) {
                                                    filterDateRanges = [...filterDateRanges, transformEra(era)];
                                                } else {
                                                    filterDateRanges = !!filters.multipleDateRanges ? filters.multipleDateRanges.filter(item => !!item.id && item.id !== era.id) : [];
                                                }
                                                if (filterDateRanges.length === eras.length) filterDateRanges = undefined;
                                                setFilters({...filters, multipleDateRanges: filterDateRanges});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(era.name, 'Unknown Era')} ({era.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                        <Panel header="Publisher" key={"publisher"}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.publishers}
                                    onChange={(event) => {
                                        setFilters({...filters, publishers: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {publishers.map((publisher,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.publishers || filters.publishers.indexOf(publisher.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.publishers && !event.target.checked) {
                                                setFilters({...filters, publishers: publishers.map(item => item.id).filter(item => item !== publisher.id)});
                                            }else if(filters.publishers) {
                                                let filtered: any = filters.publishers ? [...filters.publishers] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, publisher.id]
                                                } else {
                                                    filtered = !!filters.publishers ? filters.publishers.filter(item => item !== publisher.id) : [];
                                                }
                                                if (filtered.length === publishers.length) filtered = undefined;
                                                setFilters({...filters, publishers: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(publisher.title, 'Unknown Publisher')} ({publisher.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const SalesListFiltersDrawer = ({ filters, setFilters, listType}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['publishers', 'multipleDateRanges']);

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <SalesListFiltersContent filters={newFilters} setFilters={setNewFilters} listType={listType} />
            </IssueFilterDrawer>
        </>
    )
};

SalesListFiltersDrawer.defaultProps = defaultIssueFilterProps;

export default SalesListFiltersDrawer;